import React, { useState, useRef, useCallback, useEffect } from "react";
import { useAudio } from "../context/AudioContext";

const STEPS = 16;
const ROWS = [
  { id: "kick", label: "KK" },
  { id: "snare", label: "SN" },
  { id: "rim", label: "RM" },
  { id: "chh", label: "CH" },
  { id: "ohh", label: "OH" },
];

const DEFAULT_BPM = 120;

function createNoise(ctx, duration) {
  const bufferSize = ctx.sampleRate * duration;
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    data[i] = Math.random() * 2 - 1;
  }
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  return source;
}

function playKick(ctx, dest, time) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(dest);
  osc.frequency.setValueAtTime(150, time);
  osc.frequency.exponentialRampToValueAtTime(30, time + 0.12);
  gain.gain.setValueAtTime(0.7, time);
  gain.gain.exponentialRampToValueAtTime(0.001, time + 0.3);
  osc.start(time);
  osc.stop(time + 0.3);
}

function playSnare(ctx, dest, time) {
  const noise = createNoise(ctx, 0.15);
  const noiseGain = ctx.createGain();
  const filter = ctx.createBiquadFilter();
  filter.type = "highpass";
  filter.frequency.value = 1000;
  noise.connect(filter);
  filter.connect(noiseGain);
  noiseGain.connect(dest);
  noiseGain.gain.setValueAtTime(0.35, time);
  noiseGain.gain.exponentialRampToValueAtTime(0.001, time + 0.15);
  noise.start(time);
  noise.stop(time + 0.15);

  const osc = ctx.createOscillator();
  const oscGain = ctx.createGain();
  osc.connect(oscGain);
  oscGain.connect(dest);
  osc.frequency.setValueAtTime(200, time);
  osc.frequency.exponentialRampToValueAtTime(120, time + 0.05);
  oscGain.gain.setValueAtTime(0.35, time);
  oscGain.gain.exponentialRampToValueAtTime(0.001, time + 0.08);
  osc.start(time);
  osc.stop(time + 0.08);
}

function playRim(ctx, dest, time) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "triangle";
  osc.connect(gain);
  gain.connect(dest);
  osc.frequency.setValueAtTime(800, time);
  gain.gain.setValueAtTime(0.3, time);
  gain.gain.exponentialRampToValueAtTime(0.001, time + 0.04);
  osc.start(time);
  osc.stop(time + 0.04);
}

function playClosedHH(ctx, dest, time) {
  const noise = createNoise(ctx, 0.06);
  const gain = ctx.createGain();
  const filter = ctx.createBiquadFilter();
  filter.type = "highpass";
  filter.frequency.value = 5000;
  noise.connect(filter);
  filter.connect(gain);
  gain.connect(dest);
  gain.gain.setValueAtTime(0.2, time);
  gain.gain.exponentialRampToValueAtTime(0.001, time + 0.06);
  noise.start(time);
  noise.stop(time + 0.06);
}

function playOpenHH(ctx, dest, time) {
  const noise = createNoise(ctx, 0.25);
  const gain = ctx.createGain();
  const filter = ctx.createBiquadFilter();
  filter.type = "highpass";
  filter.frequency.value = 4000;
  noise.connect(filter);
  filter.connect(gain);
  gain.connect(dest);
  gain.gain.setValueAtTime(0.2, time);
  gain.gain.exponentialRampToValueAtTime(0.001, time + 0.25);
  noise.start(time);
  noise.stop(time + 0.25);
}

const PLAY_FN = {
  kick: playKick,
  snare: playSnare,
  rim: playRim,
  chh: playClosedHH,
  ohh: playOpenHH,
};

export default function StepSequencer() {
  const { getCtx, getMaster } = useAudio();

  const [grid, setGrid] = useState(() =>
    Object.fromEntries(ROWS.map((r) => [r.id, new Array(STEPS).fill(false)]))
  );
  const [playing, setPlaying] = useState(false);
  const [currentStep, setCurrentStep] = useState(-1);
  const [bpm, setBpm] = useState(DEFAULT_BPM);

  const intervalRef = useRef(null);
  const stepRef = useRef(0);
  const gridRef = useRef(grid);
  gridRef.current = grid;

  const toggleCell = useCallback((rowId, step) => {
    setGrid((prev) => {
      const row = [...prev[rowId]];
      row[step] = !row[step];
      return { ...prev, [rowId]: row };
    });

    const wasOn = gridRef.current[rowId][step];
    if (!wasOn) {
      const ctx = getCtx();
      const master = getMaster();
      PLAY_FN[rowId]?.(ctx, master, ctx.currentTime);
    }
  }, [getCtx, getMaster]);

  const tick = useCallback(() => {
    const ctx = getCtx();
    const master = getMaster();
    const step = stepRef.current;
    const g = gridRef.current;
    const time = ctx.currentTime;

    ROWS.forEach((row) => {
      if (g[row.id][step]) {
        PLAY_FN[row.id]?.(ctx, master, time);
      }
    });

    setCurrentStep(step);
    stepRef.current = (step + 1) % STEPS;
  }, [getCtx, getMaster]);

  const togglePlay = useCallback(() => {
    if (playing) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      setPlaying(false);
      setCurrentStep(-1);
      stepRef.current = 0;
    } else {
      getCtx();
      stepRef.current = 0;
      tick();
      const ms = (60 / bpm / 4) * 1000;
      intervalRef.current = setInterval(tick, ms);
      setPlaying(true);
    }
  }, [playing, bpm, tick, getCtx]);

  useEffect(() => {
    if (!playing) return;
    clearInterval(intervalRef.current);
    const ms = (60 / bpm / 4) * 1000;
    intervalRef.current = setInterval(tick, ms);
    return () => clearInterval(intervalRef.current);
  }, [bpm, playing, tick]);

  useEffect(() => {
    return () => clearInterval(intervalRef.current);
  }, []);

  const clearGrid = () => {
    setGrid(Object.fromEntries(ROWS.map((r) => [r.id, new Array(STEPS).fill(false)])));
  };

  return (
    <div className="w-full">
      <div className="flex flex-col gap-[3px]">
        {ROWS.map((row) => (
          <div key={row.id} className="flex items-center gap-[3px]">
            <span className="w-7 flex-shrink-0 font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--text-muted)] text-right mr-2 select-none">
              {row.label}
            </span>
            {grid[row.id].map((on, step) => {
              const isDownbeat = step % 4 === 0;
              const isCurrent = playing && step === currentStep;
              return (
                <button
                  key={step}
                  type="button"
                  onClick={() => toggleCell(row.id, step)}
                  className={[
                    "h-[20px] flex-1 rounded-[2px] transition-all duration-100 border",
                    on
                      ? isCurrent
                        ? "bg-[var(--text)] border-[var(--text)] scale-105"
                        : "bg-[var(--text)]/85 border-[var(--text)]/40"
                      : isCurrent
                        ? "bg-black/[0.08] border-black/[0.12]"
                        : isDownbeat
                          ? "bg-black/[0.04] border-black/[0.08] hover:bg-black/[0.07]"
                          : "bg-transparent border-black/[0.05] hover:bg-black/[0.04]",
                  ].join(" ")}
                  aria-label={`${row.label} step ${step + 1} ${on ? "on" : "off"}`}
                />
              );
            })}
          </div>
        ))}
      </div>

      <div className="mt-5 flex items-center gap-5">
        <button
          type="button"
          onClick={togglePlay}
          className="grid h-7 w-7 place-items-center rounded-full border border-[var(--border-strong)] text-[var(--text-secondary)] hover:text-[var(--text)] hover:border-[var(--text-tertiary)] transition-colors"
          aria-label={playing ? "Stop" : "Play"}
        >
          {playing ? (
            <svg width="8" height="8" viewBox="0 0 24 24" fill="currentColor">
              <rect x="4" y="4" width="16" height="16" rx="1" />
            </svg>
          ) : (
            <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor" className="translate-x-[1px]">
              <path d="M8 5v14l11-7-11-7z" />
            </svg>
          )}
        </button>

        <div className="flex items-center gap-2.5">
          <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--text-muted)] select-none">BPM</span>
          <input
            type="range"
            min="60"
            max="200"
            value={bpm}
            onChange={(e) => setBpm(Number(e.target.value))}
            className="track-range modal-range w-20 sm:w-24"
            aria-label="Tempo"
          />
          <span className="font-mono text-[10.5px] tabular-nums text-[var(--text-secondary)] w-7 select-none">
            {bpm}
          </span>
        </div>

        <button
          type="button"
          onClick={clearGrid}
          className="font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--text-muted)] hover:text-[var(--text)] transition-colors select-none"
        >
          Clear
        </button>
      </div>
    </div>
  );
}
